/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.cache.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-CACHE-001
 * PURPOSE: Module-scope LRU cache of email "list summary" rows so
 *          useEmail can paint a clicked row synchronously
 *          (remember/getCachedEmailSummary + reset helpers).
 * WHY: Extracted from the useEmails god-hook (ARC EMAIL_HUB_LOADING_
 *      RESILIENCE phase 3). Self-contained module state + pure
 *      accessors — zero behaviour change. rememberEmailSummary /
 *      getCachedEmailSummary are imported by the hooks; the two reset
 *      helpers are re-exported from ./useEmails for the public surface.
 * RELATED:
 *   - ./useEmails.ts: imports accessors, re-exports reset helpers.
 *   - useEmails.progressive.test.ts: pins the cache contract.
 */
import type { Email } from './useEmails.types'
import { clearPersistedInboxLists } from './useEmails.listCache'
import { clearPersistedEmailMeta } from './useEmails.metaCache'

// ---------------------------------------------------------------------------
// Email→Quote ARC § 3.3 (P3) — progressive renderer support.
//
// Module-scope cache of the "list summary" payload (subject, from,
// preview, attachment count, tags, importance, allocation_summary)
// for every email row that has passed through ``useEmails``. The cache
// lets ``useEmail`` seed its initial state synchronously when the user
// clicks an inbox row — the EmailReader can paint header+sender+tags
// + a body skeleton in the same frame as the click, instead of
// blanking the right pane until the detail GET resolves (~200-800 ms
// p50, longer on cold caches).
//
// The cache is a plain ``Map``; the keys are email IDs (strings). The
// values are the same ``Email`` shape but with body_html/body_text
// intentionally absent (the list serializer doesn't return them).
// Detail fetches always overwrite the cache with the full row so
// subsequent re-mounts get the richer payload — but the initial mount
// still gets the *something* it needs to paint immediately.
//
// Cache eviction: bounded to ``EMAIL_LIST_CACHE_MAX`` entries (LRU
// by insertion order — the oldest entry is evicted when the cache
// fills). 1024 is large enough to hold every visible inbox row + a
// generous lookback buffer; small enough that the FE heap impact is
// trivial (~256 KB at the upper bound, since each summary is well
// under 256 B).
// ---------------------------------------------------------------------------

const EMAIL_LIST_CACHE_MAX = 1024
const g_emailListCache = new Map<string, Email>()

export function rememberEmailSummary(row: Email): void {
  if (!row || !row.id) return
  // Merge over any previously-cached row rather than clobbering it.
  //
  // BUG-2026-06-15-EMAIL-READER-CC-BCC-FLICKER: this cache is written from
  // TWO paths — the rich DETAIL GET (useEmail, full row incl. cc_addresses,
  // bcc_addresses, body_html/text, attachments, external_links,
  // calendar_event, quote_request_extraction) AND the LEAN inbox LIST fetch
  // (useEmails, which omits all of those keys). A plain overwrite let a
  // background list refetch replace a full detail row with the lean list
  // row, so the next time the operator opened that email the EmailReader
  // re-seeded from the lean row and the Cc/Bcc lines + body flickered away
  // until the detail GET re-ran — the "sometimes I see the new view,
  // sometimes the old" report.
  //
  // Shallow-merging `{ ...existing, ...row }` keeps detail-only fields the
  // incoming row omits, while still letting any key the incoming row DOES
  // carry win (fresh is_read / allocation_summary / tags from the list, or
  // the authoritative body from a detail GET). The list serializer omits
  // detail-only keys entirely (absent, not `undefined`), so the spread
  // never erases them.
  const existing = g_emailListCache.get(row.id)
  const merged: Email = existing ? { ...existing, ...row } : row
  // Refresh LRU position: delete + re-set bumps the row to the end of
  // the insertion-order iteration, so the eviction below kicks out
  // truly oldest entries rather than just-touched ones.
  if (g_emailListCache.has(row.id)) g_emailListCache.delete(row.id)
  g_emailListCache.set(row.id, merged)
  while (g_emailListCache.size > EMAIL_LIST_CACHE_MAX) {
    const oldestKey = g_emailListCache.keys().next().value
    if (oldestKey === undefined) break
    g_emailListCache.delete(oldestKey)
  }
}

export function getCachedEmailSummary(id: string | null): Email | null {
  if (!id) return null
  return g_emailListCache.get(id) ?? null
}

/** Test-only escape hatch. Clears the module-scope cache so each
 *  test can assert its own seed/no-seed behaviour without
 *  cross-contamination. Not exported as part of the public surface
 *  of useEmails (the named export is consumed only by tests). */
export function __resetEmailListCacheForTests(): void {
  g_emailListCache.clear()
}

/** Render-safe org-switch step: clear only in-memory summaries. */
export function resetEmailSummaryMemoryCache(): void {
  g_emailListCache.clear()
}

/** Production reset of the email-list summary cache. Called by the
 *  cross-tab identity-change receiver
 *  (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT) so a sibling tab that
 *  was rendering the previous user's emails does not silently leak
 *  those summaries into the new user's UI while the page reloads.
 *  Distinct from the `__reset...ForTests` helper above so the
 *  production reset path is greppable + intentionally named. */
export function resetEmailListCache(): void {
  resetEmailSummaryMemoryCache()
  // Also drop the persisted SWR inbox snapshot — an identity change /
  // logout must not let the next mount paint the previous session's rows.
  clearPersistedInboxLists()
  // …and the accounts/facets meta snapshots (PERF-2026-07-08-EMAIL-
  // BOOT-META-CACHE) for the same reason.
  clearPersistedEmailMeta()
}
